import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import LoadingIndicator from "../Components/LoadingIndicator";

const CategoryInterestedUser = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [appliedCategories, setAppliedCategories] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [limit, setLimit] = useState(25);

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.post(
        API_URL,
        { action: "getActiveCategories" },
        { headers: { "Content-Type": "application/json" } },
      );

      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setCategoryOptions(
        list.map((c) => ({ value: c._id, label: c.category })),
      );
    } catch (error) {
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, limit, appliedSearch, appliedCategories, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "getuserCategories",
        page: String(currentPage),
        limit: String(limit),
        search: appliedSearch || undefined,
        sortBy,
        sortOrder,
      };

      if (appliedCategories.length > 0) {
        payload.categories = appliedCategories;
      }

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const data = response.data;
      setRows(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalCount(data.pagination?.total || 0);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handleApplyFilter = () => {
    setAppliedCategories(selectedCategories.map((c) => c.value));
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setSelectedCategories([]);
    setAppliedCategories([]);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleExport = async () => {
    try {
      const payload = {
        action: "categoryExcel",
        categories: selectedCategories.map((c) => c.value),
      };

      const res = await axios.post(API_URL, payload);

      const base64 = res.data.body;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);

      const link = document.createElement("a");
      link.href = url;
      link.download = `category-report-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to export category report");
    }
  };

  const handleSort = (column) => {
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortBy("createdAt");
      setSortOrder("desc");
    }

    setCurrentPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <i
          className="fa-solid fa-sort"
          style={{
            fontSize: "10px",
            opacity: 0.4,
            marginLeft: 5,
          }}
        />
      );
    }

    return sortOrder === "asc" ? (
      <i
        className="fa-solid fa-sort-up"
        style={{
          fontSize: "10px",
          marginLeft: 5,
        }}
      />
    ) : (
      <i
        className="fa-solid fa-sort-down"
        style={{
          fontSize: "10px",
          marginLeft: 5,
        }}
      />
    );
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <div className="d-flex align-items-center">
            <span className="mr-2">Show</span>

            <select
              className="form-control"
              style={{ width: "80px" }}
              value={limit}
              onChange={handleLimitChange}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span className="ml-2 mr-3">Entries</span>

            <span className="pagination-info">
              Showing {rows.length ? (currentPage - 1) * limit + 1 : 0} to{" "}
              {Math.min(currentPage * limit, totalCount)} of {totalCount}{" "}
              entries
            </span>
          </div>
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? "active" : ""}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Category Interested Users</h1>
            <p>View users interested in selected categories.</p>
          </div>
          <div className="col-sm-6 text-right">
            <button
              className="btn btn-success"
              onClick={handleExport}
              disabled={isLoading}
            >
              <i className="fas fa-file-excel mr-2"></i> Export
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          <div className="row mb-3 align-items-end">
            <div className="col-lg-5 col-md-5">
              <label className="font-weight-bold mb-1">Search</label>
              <form onSubmit={handleSearch}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <div className="input-group-append">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      <i className="fas fa-search"></i>
                    </button>

                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClearSearch}
                        disabled={isLoading}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Category */}
            <div className="col-lg-3 col-md-3">
              <label className="font-weight-bold mb-1">Category</label>
              <Select
                isMulti
                options={categoryOptions}
                value={selectedCategories}
                onChange={setSelectedCategories}
                placeholder="Select..."
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>

            <div className="col-lg-3 col-md-3">
              <button
                className="btn btn-primary mr-2"
                onClick={handleApplyFilter}
                disabled={isLoading}
              >
                Apply
              </button>
              {selectedCategories.length > 0 && (
                <button
                  className="btn btn-secondary"
                  onClick={handleClearFilter}
                  disabled={isLoading}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No users found.</div>
          ) : (
            <>
              <div className="employee-table-wrapper">
                <table className="table table-borderless employee-table subscription-table">
                  <thead>
                    <tr>
                      <th>Sl No.</th>

                      <th
                        onClick={() => handleSort("name")}
                        style={{ cursor: "pointer" }}
                      >
                        Name {renderSortIcon("name")}
                      </th>

                      <th
                        onClick={() => handleSort("email")}
                        style={{ cursor: "pointer" }}
                      >
                        Email {renderSortIcon("email")}
                      </th>

                      <th>Phone</th>

                      <th
                        onClick={() => handleSort("companyname")}
                        style={{ cursor: "pointer" }}
                      >
                        Company {renderSortIcon("companyname")}
                      </th>

                      <th>Interested Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row._id || idx}>
                        <td>{(currentPage - 1) * limit + idx + 1}</td>
                        <td>{row.name || "-"}</td>
                        <td>{row.email || "-"}</td>
                        <td>{row.phone || "-"}</td>
                        <td>{row.companyname || "-"}</td>
                        <td>
                          {row.interestedCategory
                            ?.map((c) => c.category)
                            .join(", ") || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryInterestedUser;
